import React from 'react';
import {
  DEAD_MIN_DAYS,
  EXPIRING_MAX_DAYS,
  EXPIRING_MIN_DAYS,
} from '../utils/portHelpers';

/**
 * Port Search and Filter Controls Component
 */
export default function PortSearch({
  searchTerm,
  handleSearchChange,
  clearSearch,
  showUnassigned,
  sortByOldestMsg,
  setSortByOldestMsg,
  lifecycleFilter,
  lifecycleCounts,
  toggleLifecycleFilter,
  clearLifecycleFilter,
  handleExportCSV,
  gateways = [],
  selectedGatewayId,
  handleGatewayFilterChange,
  clearGatewayFilter,
  selectedSourceFilter,
  handleSourceFilterChange,
  clearSourceFilter,
  selectedConversionFilter,
  handleConversionFilterChange,
  clearConversionFilter,
  NO_BOX_FILTER = '__none__',
  selectedMobileOperator,
  mobileOperatorOptions = { names: [], hasUnknown: false },
  UNKNOWN_OPERATOR = '__unknown__',
  handleMobileOperatorFilterChange,
  clearMobileOperatorFilter,
}) {
  const lifecycleButtons = [
    {
      key: 'unused',
      label: 'Unused',
      title: 'SIMs that have never sent a message — need first keep-alive',
      activeClass: 'btn-info',
      outlineClass: 'btn-outline-info',
      count: lifecycleCounts.unused,
    },
    {
      key: 'expiring',
      label: 'Expiring Soon',
      title: `${EXPIRING_MIN_DAYS}–${EXPIRING_MAX_DAYS} days since last message — send keep-alive before deactivation`,
      activeClass: 'btn-warning',
      outlineClass: 'btn-outline-warning',
      count: lifecycleCounts.expiring,
    },
    {
      key: 'dead',
      label: 'Dead',
      title: `${DEAD_MIN_DAYS}+ days since last message — likely deactivated, review for replacement`,
      activeClass: 'btn-danger',
      outlineClass: 'btn-outline-danger',
      count: lifecycleCounts.dead,
    },
  ];

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-wrap align-items-center gap-2">
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder='Search ports (e.g. "44G", "44", email, number)'
        value={searchTerm}
        onChange={handleSearchChange}
        style={{ width: '220px' }}
      />

      <select
        className="form-select form-select-sm"
        value={selectedGatewayId || ''}
        onChange={(e) => handleGatewayFilterChange(e.target.value)}
        style={{ width: '200px' }}
        title="Filter by gateway"
      >
        <option value="">All Gateways</option>
        <option value={NO_BOX_FILTER}>No box / service</option>
        {gateways.map((gateway) => (
          <option key={gateway.id} value={gateway.id}>
            {gateway.name || `Gateway ${gateway.id}`}
          </option>
        ))}
      </select>

      {selectedGatewayId && (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={clearGatewayFilter}
          title="Clear gateway filter"
        >
          Clear Gateway
        </button>
      )}

      <select
        className="form-select form-select-sm"
        value={selectedSourceFilter || ''}
        onChange={(e) => handleSourceFilterChange(e.target.value)}
        style={{ width: '160px' }}
        title="Filter by number source"
      >
        <option value="">All sources</option>
        <option value="gateway">Gateway</option>
        <option value="service">Service</option>
      </select>

      {selectedSourceFilter && (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={clearSourceFilter}
          title="Clear source filter"
        >
          Clear source
        </button>
      )}

      <select
        className="form-select form-select-sm"
        value={selectedConversionFilter || ''}
        onChange={(e) => handleConversionFilterChange(e.target.value)}
        style={{ width: '210px' }}
        title="Filter by SIM conversion status"
      >
        <option value="">All conversion</option>
        <option value="requested">Requested</option>
        <option value="converted">Converted</option>
        <option value="not_converted">Not converted</option>
      </select>

      {selectedConversionFilter && (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={clearConversionFilter}
          title="Clear conversion filter"
        >
          Clear conversion
        </button>
      )}

      <select
        className="form-select form-select-sm"
        value={selectedMobileOperator || ''}
        onChange={(e) => handleMobileOperatorFilterChange(e.target.value)}
        style={{ width: '200px' }}
        title="Filter by mobile operator"
      >
        <option value="">All Operators</option>
        {mobileOperatorOptions.hasUnknown && (
          <option value={UNKNOWN_OPERATOR}>Unknown</option>
        )}
        {mobileOperatorOptions.names.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>

      {selectedMobileOperator && (
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={clearMobileOperatorFilter}
          title="Clear mobile operator filter"
        >
          Clear Operator
        </button>
      )}

      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={clearSearch}
      >
        Clear
      </button>

      <button
        className="btn btn-primary btn-sm"
        onClick={showUnassigned}
      >
        Show Unassigned
      </button>

      <button
        className={`btn btn-sm ${
          sortByOldestMsg
            ? 'btn-warning'
            : 'btn-outline-warning'
        }`}
        onClick={() =>
          setSortByOldestMsg((prev) => !prev)
        }
        title="Sort by oldest last message first"
      >
        {sortByOldestMsg
          ? 'Sorted: Oldest Msg ↑'
          : 'Sort Oldest Msg ↑'}
      </button>

      <button
        className="btn btn-outline-info btn-sm"
        onClick={handleExportCSV}
        title="Download CSV of current results"
      >
        Export CSV
      </button>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="text-muted" style={{ fontSize: '11px', fontWeight: 600 }}>
          SIM status:
        </span>
        {lifecycleButtons.map(({ key, label, title, activeClass, outlineClass, count }) => (
          <button
            key={key}
            type="button"
            className={`btn btn-sm ${
              lifecycleFilter === key ? activeClass : outlineClass
            }`}
            onClick={() => toggleLifecycleFilter(key)}
            title={title}
          >
            {label} ({count})
          </button>
        ))}
        {lifecycleFilter && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={clearLifecycleFilter}
            title="Clear SIM status filter"
          >
            Clear status filter
          </button>
        )}
      </div>
    </div>
  );
}


